import {
  AlertCircle,
  Building2,
  CheckSquare,
  LayoutGrid,
  Layers,
  List,
  Search,
  Settings,
  SlidersHorizontal,
  User,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../components/ui/button";
import { CompanyLogo, getDomainFromCompany } from "../../../../components/ui/CompanyLogo";
import { getGlobalSearch, GlobalSearchTypes } from "../../../../services/globalSearchEndpoint";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { Input } from "../../../../components/ui/input";
import type {
  TaskDisplayMode,
  TaskPriorityFilter,
  TaskBoardStatusFilter,
  TaskRecord,
} from "../tasksTypes";

type TasksHeaderProps = {
  displayMode: TaskDisplayMode;
  onDisplayModeChange: (value: TaskDisplayMode) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  priorityFilter: TaskPriorityFilter;
  onPriorityFilterChange: (value: TaskPriorityFilter) => void;
  boardStatusFilter: TaskBoardStatusFilter;
  onBoardStatusFilterChange: (value: TaskBoardStatusFilter) => void;
  tasks: TaskRecord[];
  accountFilter: string;
  onAccountFilterChange: (value: string) => void;
  assigneeFilter: string;
  onAssigneeFilterChange: (value: string) => void;
  members?: any[];
  memberLookup?: Record<string, { name: string; email: string; initials: string }>;
};

const priorityFilterOptions: Array<{ value: TaskPriorityFilter; label: string }> = [
  { value: "all", label: "All priorities" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const boardStatusFilterOptions: Array<{ value: TaskBoardStatusFilter; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "investigating", label: "Investigating" },
  { value: "needs_first_response", label: "Needs first response" },
  { value: "on_us", label: "On us" },
  { value: "on_them", label: "On them" },
  { value: "on_hold", label: "On hold" },
  { value: "closed", label: "Closed" },
];

const PRIORITY_DOT_COLORS: Record<string, string> = {
  high: "hsl(0, 72%, 55%)",
  medium: "hsl(38, 92%, 50%)",
  low: "hsl(212, 72%, 55%)",
};

const TasksHeader = ({
  displayMode,
  onDisplayModeChange,
  searchValue,
  onSearchChange,
  showFilters: _showFilters,
  onToggleFilters: _onToggleFilters,
  priorityFilter,
  onPriorityFilterChange,
  boardStatusFilter,
  onBoardStatusFilterChange,
  tasks,
  accountFilter,
  onAccountFilterChange,
  assigneeFilter,
  onAssigneeFilterChange,
  members = [],
  memberLookup = {},
}: TasksHeaderProps) => {
  const navigate = useNavigate();
  const params = useParams();
  const [accountSearch, setAccountSearch] = useState("");
  const [debouncedAccountSearch, setDebouncedAccountSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const workspace = useWorkspaceStore((s) => s.workspace);
  const workspaceIdentifier = workspace?.id; // global_search uses workspace UUID despite param name

  const handleAccountSearchChange = useCallback((value: string) => {
    setAccountSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedAccountSearch(value);
    }, 300);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Global search for accounts
  const { data: searchResults, isLoading: isSearching } = useQuery({
    queryKey: ["accountSearch", workspaceIdentifier, debouncedAccountSearch],
    queryFn: () =>
      getGlobalSearch({
        workspace_identifier: workspaceIdentifier!,
        search_query: debouncedAccountSearch,
        object_type: GlobalSearchTypes.ACCOUNTS,
        limit: 10,
      }),
    enabled: !!workspaceIdentifier && debouncedAccountSearch.length > 0,
    staleTime: 30_000,
  });

  const searchedAccounts = useMemo(() => {
    const results = searchResults?.data?.results?.accounts;
    if (!results) return [];
    return results.map((r) => [r.external_id, { name: r.identifier, domain: r.avatar_url || getDomainFromCompany(r.identifier) }] as [string, { name: string; domain?: string }]);
  }, [searchResults]);

  // Fallback: derive from tasks for active filter label display
  const uniqueAccounts = useMemo(() => {
    const map = new Map<string, { name: string; domain?: string }>();
    for (const t of tasks) {
      if (t.account_external_id && t.account_name) {
        map.set(t.account_external_id, {
          name: t.account_name,
          domain: t.account_domain || getDomainFromCompany(t.account_name),
        });
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [tasks]);

  // Use search results when available, fall back to task-derived list
  const displayedAccounts = searchedAccounts.length > 0 || debouncedAccountSearch
    ? searchedAccounts
    : (searchResults ? searchedAccounts : uniqueAccounts.slice(0, 10));

  const uniqueAssignees = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) {
      if (t.assigned_to) set.add(t.assigned_to);
    }
    return Array.from(set).sort();
  }, [tasks]);

  // Compute active filters for badge display
  const activeFilters: Array<{ label: string; onClear: () => void }> = [];
  if (accountFilter !== "all") {
    const acct = uniqueAccounts.find(([id]) => id === accountFilter);
    activeFilters.push({
      label: `Account: ${acct ? acct[1].name : accountFilter}`,
      onClear: () => onAccountFilterChange("all"),
    });
  }
  if (assigneeFilter !== "all") {
    const assigneeName = assigneeFilter === "unassigned" ? "Unassigned" : (memberLookup[assigneeFilter]?.name || assigneeFilter);
    activeFilters.push({
      label: `Assignee: ${assigneeName}`,
      onClear: () => onAssigneeFilterChange("all"),
    });
  }
  if (boardStatusFilter !== "all") {
    const opt = boardStatusFilterOptions.find((o) => o.value === boardStatusFilter);
    activeFilters.push({
      label: `Status: ${opt?.label || boardStatusFilter}`,
      onClear: () => onBoardStatusFilterChange("all"),
    });
  }
  if (priorityFilter !== "all") {
    const opt = priorityFilterOptions.find((o) => o.value === priorityFilter);
    activeFilters.push({
      label: `Priority: ${opt?.label || priorityFilter}`,
      onClear: () => onPriorityFilterChange("all"),
    });
  }

  const hasActiveFilters = activeFilters.length > 0;

  return (
  <div className="flex flex-col border-b border-border bg-background">
    <div className="flex items-center justify-between px-6 py-4">
      <div className="flex items-center gap-2">
        <CheckSquare className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold text-foreground">Tasks</h1>
      </div>
    </div>

    <div className="flex items-center gap-3 px-6 pb-3">
      <DropdownMenu onOpenChange={() => { setAccountSearch(""); setDebouncedAccountSearch(""); }}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn("gap-2 text-sm", hasActiveFilters && "bg-muted")}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filter
            {hasActiveFilters && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                {activeFilters.length}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          {/* Account sub-menu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              Account
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-56 max-h-72 overflow-y-auto">
              <div className="px-2 py-1.5" onKeyDown={(e) => e.stopPropagation()}>
                <Input
                  placeholder="Search accounts..."
                  value={accountSearch}
                  onChange={(e) => handleAccountSearchChange(e.currentTarget.value)}
                  className="h-7 text-xs"
                />
              </div>
              <DropdownMenuItem onSelect={() => onAccountFilterChange("all")}>
                <span className={cn("text-sm", accountFilter === "all" && "font-medium")}>
                  All accounts
                </span>
              </DropdownMenuItem>
              {isSearching && (
                <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                  Searching...
                </div>
              )}
              {!isSearching && displayedAccounts.map(([id, { name, domain }]) => (
                <DropdownMenuItem key={id} onSelect={() => onAccountFilterChange(id)} className="gap-2">
                  <CompanyLogo
                    domain={domain || ""}
                    name={name}
                    className="h-4 w-4 rounded flex-shrink-0"
                  />
                  <span className={cn("truncate text-sm", accountFilter === id && "font-medium")}>
                    {name}
                  </span>
                </DropdownMenuItem>
              ))}
              {!isSearching && displayedAccounts.length === 0 && accountSearch && (
                <div className="px-2 py-3 text-center text-xs text-muted-foreground">
                  No accounts found
                </div>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Assignee sub-menu — uses workspace members */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              Assignee
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-56 max-h-64 overflow-y-auto">
              <DropdownMenuItem onSelect={() => onAssigneeFilterChange("all")}>
                <span className={cn("text-sm", assigneeFilter === "all" && "font-medium")}>
                  All
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onAssigneeFilterChange("unassigned")}>
                <span className={cn("text-sm", assigneeFilter === "unassigned" && "font-medium")}>
                  Unassigned
                </span>
              </DropdownMenuItem>
              {members.map((member: any) => {
                const memberId = member.member_id || member.user_id;
                const name = `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email;
                const initial = (member.first_name?.[0] || member.email?.[0] || "?").toUpperCase();
                return (
                  <DropdownMenuItem key={memberId} onSelect={() => onAssigneeFilterChange(memberId)} className="gap-2">
                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground flex-shrink-0">
                      {initial}
                    </div>
                    <span className={cn("truncate text-sm", assigneeFilter === memberId && "font-medium")}>
                      {name}
                    </span>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Status sub-menu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="gap-2">
              <Layers className="h-4 w-4 text-muted-foreground" />
              Status
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-48">
              {boardStatusFilterOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() => onBoardStatusFilterChange(option.value)}
                >
                  <span className={cn("text-sm", boardStatusFilter === option.value && "font-medium")}>
                    {option.label}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Priority sub-menu */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              Priority
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-44">
              {priorityFilterOptions.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() => onPriorityFilterChange(option.value)}
                  className="gap-2"
                >
                  {option.value !== "all" && (
                    <span
                      className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: PRIORITY_DOT_COLORS[option.value] }}
                    />
                  )}
                  <span className={cn("text-sm", priorityFilter === option.value && "font-medium")}>
                    {option.label}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="flex-1" />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search tasks..."
          value={searchValue}
          onChange={(event) => onSearchChange(event.currentTarget.value)}
          className="h-8 w-64 pl-9 text-sm"
        />
      </div>

      <Button
        variant="outline"
        size="sm"
        className="gap-2 text-sm"
        onClick={() => navigate(`/home/${params.workspaceId}/settings/tasks`)}
      >
        <Settings className="h-4 w-4" />
        Configure
      </Button>

      <div className="flex items-center rounded-lg border border-border bg-muted p-0.5">
        <button
          type="button"
          onClick={() => onDisplayModeChange("list")}
          className={cn(
            "flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm font-medium transition-colors",
            displayMode === "list"
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <List className="h-3.5 w-3.5" />
          List
        </button>
        <button
          type="button"
          onClick={() => onDisplayModeChange("board")}
          className={cn(
            "flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm font-medium transition-colors",
            displayMode === "board"
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <LayoutGrid className="h-3.5 w-3.5" />
          Board
        </button>
      </div>
    </div>

    {/* Active filter badges */}
    {activeFilters.length > 0 && (
      <div className="flex flex-wrap items-center gap-2 px-6 pb-3">
        {activeFilters.map((filter) => (
          <span
            key={filter.label}
            className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground"
          >
            {filter.label}
            <button
              type="button"
              onClick={filter.onClear}
              className="ml-0.5 rounded-sm hover:bg-muted-foreground/20 p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <button
          type="button"
          onClick={() => {
            onAccountFilterChange("all");
            onAssigneeFilterChange("all");
            onBoardStatusFilterChange("all");
            onPriorityFilterChange("all");
          }}
          className="text-xs text-muted-foreground hover:text-foreground underline"
        >
          Clear all
        </button>
      </div>
    )}
  </div>
  );
};

export default TasksHeader;
